import {
  Component,
  HostListener,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { StaffingService } from 'src/app/staffing/staffing.service';
import { Observable } from 'rxjs';
import {
  LoggedInUserKeys,
  LoggedInUserService,
} from '../../service/logged-in-user.service';
import { AppUsersDto } from '../../model/app-users-dto';
import { ActivatedRoute } from '@angular/router';
import { CommonService } from '../../service/common.service';

@Component({
  selector: 'app-custom-filter-builder',
  templateUrl: './custom-filter-builder.component.html',
  styleUrls: ['./custom-filter-builder.component.scss'],
  standalone:false
})
export class CustomFilterBuilderComponent implements OnInit, OnChanges, OnDestroy {
  private ageingStepTimer: ReturnType<typeof setTimeout> | null = null;
  // Add icon states
  readonly defaultAddFieldImg = this.commonService.Images.PLUS_ICON_NEW_UX;
  readonly hoverAddFieldImg = this.commonService.Images.PLUS_ICON_NEW_UX_HOVER;
  currentAddFieldImg = this.commonService.Images.PLUS_ICON_NEW_UX;
  @Input() config: any[] = [];
  @Output() applyCustomFilter = new EventEmitter<any>();
  @Output() closeBuilder = new EventEmitter<void>();
  @Input() patchData: any = null; // Regular input
  @Input() filterType: string;
  @Output() filterSaved = new EventEmitter<void>();
  @Input() ageing: any; // e.g. { minimumAgeingLimit: any; maximumAgeingLimit: any }

  filterForm!: FormGroup;
  errorMessage: string = ''; // Holds the UI error message
  ageingFieldError: string | null = null;
  private lastToastError: string | null = null; // Prevent multiple toasts spamming on input 

  isSaving: boolean = false;
  loginUser: AppUsersDto;
  isPMViewEnabled: boolean;
  isPMViewNavigation: boolean;
  readonly defaultCloseImg = this.commonService.Images.CLOSE_SEARCH;
  readonly hoverCloseImg = this.commonService.Images.CLOSE_SEARCH_HOVER;
  currentCloseImg = this.commonService.Images.CLOSE_SEARCH; 
  readonly dropdownPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];
  openDropdown: { type: 'operator' | 'field' | 'value'; index: number } | null = null;

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService, 
    private staffingService: StaffingService,
    private readonly loggedInUserService: LoggedInUserService,
    private route: ActivatedRoute,
    public readonly commonService :CommonService
  ) {}

  ngOnInit(): void {
    this.filterForm = this.fb.group({
      filterName: ['', [Validators.required, Validators.maxLength(200)]],
      conditions: this.fb.array([this.createConditionRow('WHERE'),this.createConditionRow('AND')]),
    });
    if (this.patchData) this.patchFormValues(this.patchData);
    this.loginUser = this.loggedInUserService.getLoggedInUser(
      LoggedInUserKeys.LOGGED_IN_USER,
    );
     this.route.queryParams.subscribe((params) => {
    this.isPMViewNavigation = params['view'] === 'pmView';
  });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['patchData'] && changes['patchData'].currentValue) {
      this.patchFormValues(changes['patchData'].currentValue);
    }
  }

  get conditions(): FormArray {
    return this.filterForm.get('conditions') as FormArray;
  }

  createConditionRow(operator: string = 'AND'): FormGroup {
      return this.fb.group({
        logicalOperator: [operator],
        fieldKey: ['', Validators.required],
        value: [null, Validators.required],
        startDay: [null],
        endDay: [null],
      });
  }

  addCondition(): void {
    if (this.conditions.invalid) {
      this.conditions.markAllAsTouched();
      this.errorMessage = 'Please fill all fields before adding a new condition.';
      return;
    }

    if (this.hasDuplicateConditions()) {
      this.errorMessage = 'Duplicate filters (same field and value) are not allowed.';
      this.toastr.error('Duplicate filters (same field and value) are not allowed.');
      return;
    }

    const activeOperator = this.conditions.length > 1 
      ? this.conditions.at(1).get('logicalOperator')?.value 
      : 'AND';

    this.errorMessage = '';
    this.conditions.push(this.createConditionRow(activeOperator));
  }

  removeCondition(index: number, e: Event): void {
    e.stopPropagation();
    if (this.conditions.length > 1) {
      this.closeDropdown();
      this.conditions.removeAt(index);
      this.errorMessage = ''; // reset errors on delete
      this.validateAllAgeingFields(); // re-verify any active ageing fields 
    }
  }

  onFieldChange(index: number) {
    this.conditions.at(index).patchValue({ value: null, startDay: null, endDay: null });
    this.validateAllAgeingFields();
  }

  startAgeingStep(event: PointerEvent, input: HTMLInputElement, direction: 1 | -1): void {
    if (event.button !== 0 || input.disabled || input.readOnly) return;
    event.preventDefault();
    this.stopAgeingStep();
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.stepAgeingDay(input, direction);
    const repeat = () => {
      this.stepAgeingDay(input, direction);
      this.ageingStepTimer = setTimeout(repeat, 75);
    };
    this.ageingStepTimer = setTimeout(repeat, 400);
  }

  stopAgeingStep(): void {
    if (this.ageingStepTimer !== null) {
      clearTimeout(this.ageingStepTimer);
      this.ageingStepTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.stopAgeingStep();
  }

  stepAgeingDay(input: HTMLInputElement, direction: 1 | -1): void {
    if (input.disabled || input.readOnly) return;

    const previousValue = input.value;
    if (direction === 1) input.stepUp();
    else input.stepDown();

    input.focus();
    if (input.value !== previousValue) {
      // Native stepping does not emit input; notify Angular's number value
      // accessor and the existing range validation just like manual entry.
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  onAgeingRangeChange(index: number): void {
    const condition = this.conditions.at(index);
    const startDay = condition.get('startDay')?.value;
    const endDay = condition.get('endDay')?.value;
    const hasBothDays = startDay !== null && startDay !== '' &&
      endDay !== null && endDay !== '';

    condition.get('value')?.setValue(hasBothDays
      ? { startDay: Number(startDay), endDay: Number(endDay) }
      : null);
    this.validateAllAgeingFields();
  }

  isDropdownOpen(type: 'operator' | 'field' | 'value', index: number): boolean {
    return this.openDropdown?.type === type && this.openDropdown?.index === index;
  }

  toggleDropdown(
    type: 'operator' | 'field' | 'value',
    index: number,
    event: Event,
  ): void {
    event.stopPropagation();
    this.openDropdown = this.isDropdownOpen(type, index)
      ? null
      : { type, index };
  }

  onDropdownDetached(type: 'operator' | 'field' | 'value', index: number): void {
    // Hiding a parent can detach the overlay during change detection. Clear
    // its state after that pass, without affecting a different menu just opened.
    queueMicrotask(() => {
      if (this.isDropdownOpen(type, index)) this.closeDropdown();
    });
  }

  handleDropdownKeydown(
    event: KeyboardEvent,
    type: 'operator' | 'field' | 'value',
    index: number,
  ): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleDropdown(type, index, event);
    } else if (event.key === 'ArrowDown' && !this.isDropdownOpen(type, index)) {
      event.preventDefault();
      this.toggleDropdown(type, index, event);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDropdown();
    }
  }

  selectOperator(operator: 'AND' | 'OR', index: number, event: Event): void {
    event.stopPropagation();
    this.conditions.at(index).get('logicalOperator')?.setValue(operator);
    this.conditions.at(index).get('logicalOperator')?.markAsTouched();
    this.onOperatorChange(index);
    this.openDropdown = null;
  }

  selectField(fieldKey: string, index: number, event: Event): void {
    event.stopPropagation();
    if (this.isFieldRestricted(fieldKey, index)) return;

    this.conditions.at(index).get('fieldKey')?.setValue(fieldKey);
    this.conditions.at(index).get('fieldKey')?.markAsTouched();
    this.onFieldChange(index);
    this.openDropdown = null;
  }

  selectValue(value: unknown, index: number, event: Event): void {
    event.stopPropagation();
    const control = this.conditions.at(index).get('value');
    // Native <option [value]> values were strings; retain that payload contract.
    control?.setValue(String(value));
    control?.markAsTouched();
    this.errorMessage = '';
    this.validateAllAgeingFields();
    this.openDropdown = null;
  }

  getFieldLabel(fieldKey: unknown): string {
    if (!fieldKey) return 'Select Field';
    return this.getFieldOptions().find((option) => option.key === fieldKey)?.label ?? String(fieldKey);
  }

  getFieldOptions(): any[] {
    return (this.config ?? []).filter(Boolean);
  }

  getValueLabel(activeConfig: any, value: unknown): string {
    if (value === null || value === undefined || value === '') return 'Select Value';

    const options = activeConfig?.type === 'select-object'
      ? activeConfig.objectOptions ?? []
      : activeConfig.options ?? [];
    const matchedOption = options.find((option: any) => {
      const optionValue = activeConfig?.type === 'select-object'
        ? option[activeConfig.valueKey || 'id']
        : option?.value !== undefined
          ? option.value
          : option;
      return String(optionValue) === String(value);
    });

    if (matchedOption === undefined) return String(value);
    return activeConfig?.type === 'select-object'
      ? String(matchedOption[activeConfig.displayKey || 'name'])
      : String(matchedOption?.label !== undefined ? matchedOption.label : matchedOption);
  }

  getOptionValue(activeConfig: any, option: any): unknown {
    return activeConfig?.type === 'select-object'
      ? option[activeConfig.valueKey || 'id']
      : option?.value !== undefined
        ? option.value
        : option;
  }

  getOptionLabel(activeConfig: any, option: any): string {
    return activeConfig?.type === 'select-object'
      ? String(option[activeConfig.displayKey || 'name'])
      : String(option?.label !== undefined ? option.label : option);
  }

  getValueOptions(activeConfig: any): any[] {
    const options = activeConfig?.type === 'select-object'
      ? activeConfig.objectOptions ?? []
      : activeConfig.options ?? [];
    return options.filter(Boolean);
  }

  valuesEqual(first: unknown, second: unknown): boolean {
    return String(first) === String(second);
  }

  @HostListener('document:click')
  closeDropdown(): void {
    this.openDropdown = null;
  }

  resetAll(): void {
    this.filterForm.reset();
    this.conditions.clear();
    this.errorMessage = '';
    this.ageingFieldError = null;
    this.lastToastError = null;
    this.openDropdown = null;
    this.conditions.push(this.createConditionRow('WHERE'));
    this.conditions.push(this.createConditionRow('AND'));
  }

  getFieldConfig(key: string): any {
    if (!key) return null;
    return this.getFieldOptions().find((c) => c.key === key);
  }

  get activeLogicCombinationText(): string {
    const conditionValues = this.conditions.value;
    if (conditionValues.length <= 1) return '';

    const operators = conditionValues
      .slice(1)
      .map((c: any) => c.logicalOperator);
    const hasAnd = operators.includes('AND');
    const hasOr = operators.includes('OR');

    if (hasAnd && hasOr) return 'Combination of ‘AND/OR’ filter is on.';
    if (hasAnd && !hasOr) return 'Combination of ‘AND’ filter is on.';
    if (!hasAnd && hasOr) return 'Combination of ‘OR’ filter is on.';

    return '';
  }

  saveFilter(): void {
    this.errorMessage = '';

    if (
      this.filterForm.invalid ||
      !this.filterForm.get('filterName')?.value?.trim()
    ) {
      this.filterForm.markAllAsTouched();
      this.errorMessage = this.filterForm.get('filterName')?.hasError('maxlength')
        ? 'Filter name cannot exceed 200 characters.'
        : 'Please fill all the fields.';
      return;
    }

    if (this.hasDuplicateConditions()) {
        this.errorMessage = 'Duplicate filters found. You cannot have identical field and value pairs.';
        this.toastr.error(this.errorMessage);
        return;
    }

    // Secondary validation block if aging fields are out of bounds 
    if (!this.validateAllAgeingFields()) {
      this.errorMessage = 'Please fix the ageing value(s) before saving.';
      return;
    }

    this.isSaving = true;
    const formVal = this.filterForm.value;

    let filterType;
    if (this.isPMViewNavigation) {
      filterType = 'PROJECT_MANAGER';
    } else if (this.filterType === 'SR_VIEW') {
      filterType = 'SR';
    } else if (this.filterType === 'BENCH_VIEW') {
      filterType = 'BENCH_EMPLOYEE';
    } 
    else if (this.filterType === 'DEMAND_TRACKER') {
      filterType = 'DEMAND_TRACKER';
    }
    else {
      filterType = 'EMPLOYEE';
    }
    console.log('formVal',formVal);

    const payload: any = {
      filterName: formVal.filterName.trim(),
      filterType,
      filterConditions: formVal.conditions.map(
        (cond: any, index: number) => ({
          field: cond.fieldKey,
          ...(cond.fieldKey === 'ageing'
            ? { startDay: cond.value.startDay, endDay: cond.value.endDay }
            : { value: cond.value }),
          operator: index === 0 ? 'WHERE' : cond.logicalOperator,
        })
      ),
    };

    if (this.patchData?.id) {
      payload.id = this.patchData.id;
    }
    console.log('filterType',filterType);

    // if (['SR_VIEW','BENCH_VIEW'].includes(this.filterType)){
      this.staffingService.saveCustomFilter(payload).subscribe(
        () => {
          this.isSaving = false;
          this.toastr.success(
            this.patchData?.id
              ? 'Filter updated successfully'
              : 'Filter saved successfully'
          );
  
          const finalFilters: any = {};
          formVal.conditions.forEach((condition: any) => {
            const field = condition.fieldKey;
            const val = condition.value;
            if (!finalFilters[field]) {
              finalFilters[field] = [];
            }
            finalFilters[field].push(val);
          });
          
          this.applyCustomFilter.emit({
            customFilterName: formVal.filterName,
            logic: this.activeLogicCombinationText,
            rawConditions: formVal.conditions,
            filters: finalFilters
          });
  
          this.filterSaved.emit();
          this.resetAll();
        },
        (error: any) => {
          this.isSaving = false;
          this.toastr.error('Failed to save filter');
          console.error('Save filter error:', error);
        }
      );
    // }

    // else if (this.filterType=='DEMAND_TRACKER'){
    //  console.log('payload',payload);
    //  console.log('demadn tracker custom filter api');
    // }

  }

  private patchFormValues(data: any): void {
    if (!this.filterForm) return;
    this.filterForm.patchValue({
      filterName: data.customFilterName ?? data.filterName,
    });
    this.conditions.clear();
    const conditions = data.rawConditions ?? data.filterConditions ?? [];
    if (conditions.length > 0) {
      conditions.forEach((cond: any, index: number) => {
        const fieldKey = cond.fieldKey ?? cond.field;
        const operator = cond.logicalOperator || cond.operator || (index === 0 ? 'WHERE' : 'AND');
        const conditionGroup = this.createConditionRow(operator);
        conditionGroup.patchValue({ ...cond, fieldKey });
        if (fieldKey === 'ageing') {
          let range: any = null;
          if (cond.startDay != null && cond.endDay != null) {
            range = { startDay: cond.startDay, endDay: cond.endDay };
          } else {
            try {
              range = typeof cond.value === 'string' ? JSON.parse(cond.value) : cond.value;
            } catch {
              range = cond.value;
            }
          }
          if (range && typeof range === 'object' && 'startDay' in range && 'endDay' in range) {
            conditionGroup.patchValue({
              startDay: Number(range.startDay),
              endDay: Number(range.endDay),
              value: { startDay: Number(range.startDay), endDay: Number(range.endDay) },
            });
          } else if (range !== null && range !== undefined && range !== '') {
            // Older saved filters used one day and an implicit two-day range.
            conditionGroup.patchValue({
              startDay: Number(range),
              endDay: Number(range) + 2,
              value: { startDay: Number(range), endDay: Number(range) + 2 },
            });
          }
        }
        this.conditions.push(conditionGroup);
      });
    } else {
      this.conditions.push(this.createConditionRow('WHERE'));
      this.conditions.push(this.createConditionRow('AND'));
    }
  }

  onOperatorChange(index: number): void {
    this.errorMessage = '';
    const conditionRow = this.conditions.at(index);
    const fieldKey = conditionRow?.get('fieldKey')?.value;
    const selectedOperator = conditionRow?.get('logicalOperator')?.value;

    if (index === 1) {
      this.conditions.controls.forEach((control, idx) => {
        if (idx > 1) {
          control.get('logicalOperator')?.setValue(selectedOperator, { emitEvent: false });
        }
      });
    }

    if (fieldKey && this.isFieldRestricted(fieldKey, index)) {
      this.conditions.clear();
      this.errorMessage = '';
      this.conditions.push(this.createConditionRow('WHERE'));
      this.conditions.push(this.createConditionRow('AND'));
      return;
    }
    
    if (this.hasDuplicateConditions()) {
      this.errorMessage = 'Duplicate filters found. Please adjust your conditions.';
    }
  }

  isFieldRestricted(fieldKey: string, currentIndex: number): boolean {
    if (!fieldKey) return false;
    const currentOp =
      currentIndex === 0
        ? 'WHERE'
        : this.conditions.at(currentIndex)?.get('logicalOperator')?.value;

    if (currentOp === 'OR') {
      return false;
    }

    const conditions = this.conditions.value;
    for (let i = 0; i < conditions.length; i++) {
      if (i === currentIndex) continue;

      const cond = conditions[i];
      if (cond.fieldKey === fieldKey) {
        const otherOp = i === 0 ? 'WHERE' : cond.logicalOperator;
        if (otherOp === 'AND' || otherOp === 'WHERE') {
          return true;
        }
      }
    }
    return false;
  }

  hasDuplicateConditions(): boolean {
    const conditions = this.conditions.value;
    const seen = new Set();
    for (const cond of conditions) {
      if (!cond.fieldKey || cond.value === null || cond.value === '') continue;
      const uniqueKey = `${cond.fieldKey}-${typeof cond.value === 'object' ? JSON.stringify(cond.value) : cond.value}`;
      if (seen.has(uniqueKey)) {
        return true;
      }
      seen.add(uniqueKey);
    }
    return false;
  }
  
  /**
   * Real-time Validation looping through all conditions
   * Triggers dynamically to validate every `ageing` field block setup, safely evaluating OR combined scenarios. 
   */
  validateAllAgeingFields(): boolean {
    let currentError: string | null = null;
    const minLimit = 0;
    const maxLimit = this.ageing?.maximumAgeingLimit ?? this.ageing?.maxAgeing;

    for (const condition of this.conditions.controls) {
      if (condition.get('fieldKey')?.value === 'ageing') {
        const startDay = condition.get('startDay')?.value;
        const endDay = condition.get('endDay')?.value;

        // Validate each entered bound, even while the other input is blank.
        const days = [startDay, endDay]
          .filter((day) => day !== null && day !== '');
        if (days.some((day) => !Number.isFinite(Number(day)) ||
            Number(day) < minLimit ||
            (maxLimit != null && Number(day) > maxLimit))) {
          currentError = `Ageing value must be between ${minLimit} and ${maxLimit}.`;
          break;
        }
        if (days.length === 2 && Number(startDay) > Number(endDay)) {
          currentError = 'Ageing start day cannot exceed end day.';
          break;
        }
      }
    }

    if (currentError) {
      this.ageingFieldError = currentError;
      if (this.lastToastError !== currentError) {
        this.lastToastError = currentError;
      }
      return false;
    }

    this.ageingFieldError = null;
    this.lastToastError = null;
    return true;
  }


}
